import { Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "store/store";
import {
  setIsPlaying,
  setCurrentlyPlayingMusicData,
} from "store/musicLibraries";
import { motion, AnimatePresence } from "framer-motion"; // Import Framer Motion
import { logoFlipFlop } from "assets";

export default function DialogMusicPlayer() {
  const dispatch = useDispatch();
  const [volume, setVolume] = useState<number>(0.5);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [showVolumeSlider, setShowVolumeSlider] = useState<boolean>(false); // Controls visibility
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { currentlyPlayingMusicData, isPlaying } = useAppSelector(
    (state) => state.musicLibraries
  );

  useEffect(() => {
    if (currentlyPlayingMusicData && audioRef.current) {
      audioRef.current
        .play()
        .then(() => {
          dispatch(setIsPlaying(true));
        })
        .catch((error) => {
          console.error("Audio play failed:", error);
        });
    }
  }, [currentlyPlayingMusicData, dispatch]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const updateTime = () => setCurrentTime(audio.currentTime);
      const updateDuration = () => setDuration(audio.duration);

      audio.addEventListener("timeupdate", updateTime);
      audio.addEventListener("loadedmetadata", updateDuration);

      return () => {
        audio.removeEventListener("timeupdate", updateTime);
        audio.removeEventListener("loadedmetadata", updateDuration);
      };
    }
  }, [currentlyPlayingMusicData]);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      await audioRef.current.play();
    }
    dispatch(setIsPlaying(!isPlaying));
  };

  const toggleMute = () => {
    setVolume(volume === 0 ? 0.5 : 0);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <AnimatePresence>
      {currentlyPlayingMusicData && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="fixed bottom-8 right-20 bg-white/20 backdrop-blur-lg shadow-lg p-4 rounded-2xl w-80 flex flex-col space-y-2 border border-white/30 z-50"
        >
          <div className="flex items-center space-x-4">
            <img
              src={currentlyPlayingMusicData?.thumbnail ?? logoFlipFlop}
              alt="img"
              className="rounded-lg w-10 h-10"
            />
            <div className="flex-1">
              <h3 className="text-sm font-semibold line-clamp-1">
                {currentlyPlayingMusicData?.sound_title ?? "Unknown Title"}
              </h3>
              <p className="text-xs line-clamp-1">
                {currentlyPlayingMusicData?.sound_singer ?? "Unknown Artist"}
              </p>
            </div>
            <button
              onClick={togglePlay}
              className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>

            <button
              onClick={() => dispatch(setCurrentlyPlayingMusicData(null))}
              className="text-gray-400 hover:text-black dark:hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress Bar & Duration */}
          <div className="flex items-center space-x-2 text-xs">
            <span>{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="w-full accent-blue-500"
            />
            <span>{formatTime(duration)}</span>
          </div>

          {/* Mute Button + Volume Slider on Hover */}
          <div
            className="relative flex items-center"
            onMouseEnter={() => setShowVolumeSlider(true)}
            onMouseLeave={() => setShowVolumeSlider(false)}
          >
            <button onClick={toggleMute} className="">
              {volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>

            {/* Volume Slider (Appears on Hover) */}
            <AnimatePresence>
              {showVolumeSlider && (
                <motion.input
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  type="range"
                  className="absolute left-8 w-24 accent-blue-500"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                />
              )}
            </AnimatePresence>
          </div>

          <audio
            ref={audioRef}
            src={currentlyPlayingMusicData?.sound_url || ""}
            autoPlay // Ensures the audio plays when the component is rendered
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
